import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { parseDate } from 'lib/date';
import { WorkflowAction, WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { INBOUND_REFERRALS_ROUTES } from './constants';
import { Pill, workflowStatusDisplay } from './StatusBadge';

// The duplicate-review body (OLIO-2729): the search surfaced other active referrals
// that may be this same patient. A person either confirms one — canceling THIS
// referral, linked to the survivor — or keeps this referral. No confidence level ever
// auto-cancels; the consequence is spelled out in a red confirmation modal.

// Value content is camelCased by the API middleware (see eligibilityData.ts).
export interface DuplicateCandidate {
  workflowId: string;
  status?: string;
  createdAt?: string;
  matchStrength: string;
  patientFirstName?: string;
  patientLastName?: string;
  patientDob?: string;
}

interface Props {
  action: WorkflowAction;
  onComplete: (actionId: string, outputs: Record<string, string>) => unknown;
  isCompleting: boolean;
}

const formatDate = (value?: string) => parseDate(value ?? null)?.toLocaleDateString() ?? '—';

export default function DuplicatePatientReview({ action, onComplete, isCompleting }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const raw = action.outputFor('duplicate_candidates');
  const candidates = Array.isArray(raw) ? (raw as DuplicateCandidate[]) : [];
  // Re-derived against the current list: a background refetch can drop the picked
  // candidate, and a confirm must never submit an id that's no longer on screen.
  const selected = candidates.find((candidate) => candidate.workflowId === selectedId);

  const confirm = () => {
    setConfirming(false);
    if (!selected) return;
    onComplete(action.id, {
      duplicate_patient_result: 'confirmed_match',
      duplicate_of_workflow_id: selected.workflowId,
    });
  };

  return (
    <Wrapper data-testid='duplicate-patient-review'>
      <div>
        <BodySmallBold>
          {candidates.length} possible existing referral{candidates.length === 1 ? '' : 's'} found for this patient
        </BodySmallBold>
        <Explanation>
          Review the matches below. If this referral duplicates one of them, select it and confirm — this referral will
          be canceled and linked to the existing one. Otherwise, keep working this referral.
        </Explanation>
      </div>

      <Cards role='radiogroup' aria-label='Possible duplicate referrals'>
        {candidates.map((candidate) => (
          <CandidateCard
            key={candidate.workflowId}
            type='button'
            role='radio'
            aria-checked={selected?.workflowId === candidate.workflowId}
            $selected={selected?.workflowId === candidate.workflowId}
            onClick={() => setSelectedId(candidate.workflowId)}
            data-testid='duplicate-candidate'>
            <CardHeader>
              <Radio $selected={selected?.workflowId === candidate.workflowId} aria-hidden />
              <PatientName>
                {candidate.patientFirstName} {candidate.patientLastName}
              </PatientName>
              <Pill $tone={candidate.matchStrength === 'strong' ? 'danger' : 'warning'}>
                {candidate.matchStrength === 'strong' ? 'Strong match' : 'Possible match'}
              </Pill>
            </CardHeader>
            <CardMetaRow>
              <CardMeta>DOB: {formatDate(candidate.patientDob)}</CardMeta>
              <MetaDivider />
              <CardMeta>Received {formatDate(candidate.createdAt)}</CardMeta>
              <MetaDivider />
              {candidate.status && (
                // The candidate's run status, in the same vocabulary the rest of the app
                // uses for referrals (StatusBadge) — e.g. action_required → "Needs Attention".
                <Pill $tone='neutral'>{workflowStatusDisplay(candidate.status as WorkflowStatus).label}</Pill>
              )}
              <ViewLink
                to={`${INBOUND_REFERRALS_ROUTES.BASE}/${candidate.workflowId}`}
                target='_blank'
                onClick={(event) => event.stopPropagation()}
                data-testid='view-candidate'>
                View referral
              </ViewLink>
            </CardMetaRow>
          </CandidateCard>
        ))}
      </Cards>

      <Footer>
        <Button
          variant='primary'
          size='small'
          disabled={isCompleting}
          onClick={() => onComplete(action.id, { duplicate_patient_result: 'no_match' })}
          data-testid='duplicate-no-match'>
          Not a Duplicate
        </Button>
        <Button
          variant='outline-danger'
          size='small'
          disabled={!selected || isCompleting}
          onClick={() => setConfirming(true)}
          data-testid='duplicate-confirm'>
          Confirm Duplicate
        </Button>
        {!selected && <FooterHint>Select a match to confirm a duplicate</FooterHint>}
      </Footer>

      {confirming && (
        <StandardModal
          title='Confirm Duplicate'
          onCancel={() => setConfirming(false)}
          colorTheme={ColorThemeName.PrimaryRed}>
          <ModalBody>
            <BodySmall>
              Are you sure this referral is a duplicate? This referral will be{' '}
              <BodySmallBold as='span'>canceled</BodySmallBold> and linked to the existing referral you selected. The
              existing referral is not changed.
            </BodySmall>
          </ModalBody>
          <ModalButtons>
            <Button variant='ghost' onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant='danger' disabled={isCompleting} onClick={confirm} data-testid='duplicate-confirm-modal'>
              Confirm Duplicate
            </Button>
          </ModalButtons>
        </StandardModal>
      )}
    </Wrapper>
  );
}

// The settled note on a completed search that confirmed a duplicate: this run was
// canceled; the link points at the surviving referral.
export function DuplicateLinkNote({ action }: { action: WorkflowAction }) {
  const survivorId = action.outputFor('duplicate_of_workflow_id');
  if (typeof survivorId !== 'string' || !survivorId) return null;

  return (
    <BodySmall data-testid='duplicate-link-note'>
      <ViewLink to={`${INBOUND_REFERRALS_ROUTES.BASE}/${survivorId}`} data-testid='duplicate-survivor-link'>
        View the existing referral
      </ViewLink>
    </BodySmall>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Explanation = styled(BodySmall)`
  color: ${colors.black50};
  margin-top: 2px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CandidateCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  text-align: left;
  font: inherit;
  background: ${({ $selected }) => ($selected ? colors.blue5 : colors.white)};
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  box-shadow: ${({ $selected }) => ($selected ? `0 0 0 1px ${colors.blue100}` : 'none')};
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.blue100};
  }
`;

const Radio = styled.span<{ $selected: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  border: ${({ $selected }) => ($selected ? `4px solid ${colors.blue100}` : `1.5px solid ${colors.black25}`)};
  background: ${colors.white};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PatientName = styled(BodySmallBold)`
  font-size: 14px;
`;

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 24px;
`;

const CardMeta = styled(BodySmall)`
  color: ${colors.black50};
`;

const MetaDivider = styled.span`
  width: 1px;
  height: 12px;
  background: ${colors.black10};
`;

const ViewLink = styled(Link)`
  color: ${colors.blue100};
  font-size: 12px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterHint = styled(BodySmall)`
  color: ${colors.black50};
`;

const ModalBody = styled.div`
  margin: 20px 0 24px;
`;

const ModalButtons = styled(ButtonGroup)`
  justify-content: flex-end;
`;
